import { createReducer } from '@reduxjs/toolkit';
import {
  listEventVendors,
  searchEventVendors,
  filterEventVendors,
  exportEventVendors,
  getEventVendorInfo,
  updateEventVendorInfo,
  addEventVendor,
} from '../actions/eventVendor';

const initialState = {
  eventVendors: {
    data: [],            // List of eventVendors (includes filtered or search results)
    currentPage: 1,      // Current page for pagination
    totalRecords: 0,       // Total number of pages
    pageSize: 10,        // Items per page
  },
  loading: false,        // Loading state for listing, filtering, searching, and exporting eventVendors
  error: null,           // General error state

  // Export state as an object
  export: {
    loading: false,      // Loading state for export action
    success: false,      // Tracks if export was successful
    error: null,         // Export-specific error
  },

  // States for eventVendor actions
  viewDetails: {
    data: null,
    error: null,
  },
  updateInfo: {
    loading: false,      // Loading state for update action
    success: false,      // Tracks if update was successful
    error: null,         // Update-specific error
  },
  addEventVendor: {
    loading: false,      // Loading state for add action
    success: false,      // Tracks if add was successful
    error: null,         // Add-specific error
  },
};

const eventVendorsReducer = createReducer(initialState, {
  // Listing eventVendors (with pagination)
  [listEventVendors.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [listEventVendors.resolved]: (state, action) => {
  
    const { 
      data: eventVendors, 
      current_page: currentPage, 
      next_page_url: nextPageUrl, 
      prev_page_url: prevPageUrl, 
      total: totalRecords, 
      per_page: pageSize 
    } = action.payload;


    state.loading = false;
    state.eventVendors = {
      data: eventVendors,
      currentPage,
      totalRecords, 
      pageSize,  
      nextPageUrl,
      prevPageUrl   
    };
    
  },
  [listEventVendors.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle listing error
  },

  // Filter eventVendors
  [filterEventVendors.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [filterEventVendors.resolved]: (state, action) => {
    const { eventVendors, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.eventVendors = {
      data: eventVendors,    // Set filtered eventVendors data
      currentPage,        // Update pagination after filtering
      totalRecords,
      pageSize,
    };
  },
  [filterEventVendors.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle filtering error
  },

  // Search eventVendors
  [searchEventVendors.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [searchEventVendors.resolved]: (state, action) => {
    const { eventVendors, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.eventVendors = {
      data: eventVendors,    // Set search results
      currentPage,        // Update pagination after search
      totalRecords,
      pageSize,
    };
  },
  [searchEventVendors.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle search error
  },

  // Export eventVendors
  [exportEventVendors.trigger]: (state) => {
    state.export.loading = true;  // Set export-specific loading
    state.export.success = false; // Reset success status
    state.export.error = null;    // Clear export errors
  },
  [exportEventVendors.resolved]: (state) => {
    state.export.loading = false; // Stop export loading
    state.export.success = true;  // Set export success flag
  },
  [exportEventVendors.rejected]: (state, action) => {
    state.export.loading = false; // Stop export loading
    state.export.success = false; // Mark export as unsuccessful
    state.export.error = action.payload; // Handle export error
  },

  // View eventVendor details
  [getEventVendorInfo.trigger]: (state) => {
    state.viewDetails.error = null;
  },
  [getEventVendorInfo.resolved]: (state, action) => {
    state.viewDetails.data = action.payload; // Set eventVendor details
  },
  [getEventVendorInfo.rejected]: (state, action) => {
    state.viewDetails.error = action.payload; // Handle view details error
  },

  // Update eventVendor info
  [updateEventVendorInfo.trigger]: (state) => {
    state.updateInfo.loading = true;  // Set update-specific loading
    state.updateInfo.success = false; // Reset success status
    state.updateInfo.error = null;    // Clear update errors
  },
  [updateEventVendorInfo.resolved]: (state) => {
    state.updateInfo.loading = false; // Stop update loading
    state.updateInfo.success = true;  // Set update success flag
  },
  [updateEventVendorInfo.rejected]: (state, action) => {
    state.updateInfo.loading = false; // Stop update loading
    state.updateInfo.success = false; // Mark update as unsuccessful
    state.updateInfo.error = action.payload; // Handle update error
  },

  // Add eventVendor
  [addEventVendor.trigger]: (state) => {
    state.addEventVendor.loading = true;  // Set add-specific loading
    state.addEventVendor.success = false; // Reset success status
    state.addEventVendor.error = null;    // Clear add errors
  },
  [addEventVendor.resolved]: (state) => {
    state.addEventVendor.loading = false; // Stop add loading
    state.addEventVendor.success = true;  // Set add success flag
  },
  [addEventVendor.rejected]: (state, action) => {
    state.addEventVendor.loading = false; // Stop add loading
    state.addEventVendor.success = false; // Mark add eventVendor as unsuccessful
    state.addEventVendor.error = action.payload; // Handle add eventVendor error
  },
});

export default eventVendorsReducer;